// Pure helpers for the MergeReviewWizard.
//
// Kept React-free + side-effect-free so the unit tests in
// `tests/web/merge-review-helpers.test.ts` can exercise every branch
// of the override-composition logic without standing up DOM / D1.
//
// Surface summary:
//   * MERGEABLE_FIELDS_UI — display-ordered list of mergeable fields
//     with human labels. Mirrors the server's MERGEABLE_FIELDS const
//     in shared/db/contact-merge.ts (keep in sync — there's a
//     compile-time check below that fires if they drift).
//   * detectConflicts() — given a survivor + a merged contact, return
//     the subset of mergeable fields where their values differ.
//   * defaultPerFieldChoice() — Grant 2026-05-05 smart default:
//     when survivor is blank but merged has a value, pre-pick
//     "merged" so the wizard surfaces the populated value as the
//     winner. Otherwise default "primary" (do nothing).
//   * composeMergeRequest() — turn the wizard's per-field choice
//     map (including "keep_both" on email + phone_primary) into the
//     full merge request shape (field_overrides + secondary_promotions)
//     the merge endpoint expects.
//   * detectCrossHousehold() — check whether all cluster members
//     share one household_id.
//   * countPopulatedFields() — for the survivor-pick card. Counts
//     non-null, non-empty mergeable fields on the contact.

import type {
  ContactFieldOverrides,
  MergeableContactField,
} from "../../../shared/db/contact-merge";

// Structural type accepted by the helpers. The component layer
// passes a SafeContact in; tests pass a hand-rolled fixture. We
// intentionally do NOT import SafeContact here so this module (and
// its test) typecheck under the worker root tsconfig that excludes
// DOM types.
export type MergeContactShape = Readonly<
  Partial<Record<MergeableContactField, unknown>> & {
    household_id?: string | null;
  }
>;

// Display order for the wizard's per-field review table. A copy of
// the server's MERGEABLE_FIELDS array reordered for human-readable
// grouping (identity → contact → address → demographics → comms).
// The compile-time check at the bottom of the file ensures this list
// stays a strict subset of the server's allowlist.
export const MERGEABLE_FIELDS_UI: ReadonlyArray<{
  key: MergeableContactField;
  label: string;
  group: "identity" | "contact" | "address" | "demographics" | "comms";
}> = [
  { key: "legal_first_name", label: "Legal first name", group: "identity" },
  { key: "legal_middle_name", label: "Legal middle name", group: "identity" },
  { key: "legal_last_name", label: "Legal last name", group: "identity" },
  { key: "legal_suffix", label: "Suffix", group: "identity" },
  { key: "preferred_name", label: "Preferred name", group: "identity" },
  { key: "email", label: "Email", group: "contact" },
  { key: "secondary_email", label: "Secondary email", group: "contact" },
  { key: "phone_primary", label: "Primary phone", group: "contact" },
  { key: "phone_secondary", label: "Secondary phone", group: "contact" },
  { key: "street_line_1", label: "Street", group: "address" },
  { key: "street_line_2", label: "Apt / unit", group: "address" },
  { key: "city", label: "City", group: "address" },
  { key: "state", label: "State", group: "address" },
  { key: "zip", label: "ZIP", group: "address" },
  { key: "birthday", label: "Date of birth", group: "demographics" },
  { key: "gender", label: "Gender", group: "demographics" },
  { key: "marital_status", label: "Marital status", group: "demographics" },
  { key: "preferred_pharmacy", label: "Preferred pharmacy", group: "demographics" },
  // S7 Planning underwriting facts — surfaced in the merge review for the
  // same reason they are mergeable at all: losing a client's recorded
  // tobacco answer or income to a merge means asking them for it again.
  { key: "tobacco_use", label: "Tobacco use", group: "demographics" },
  { key: "height_inches", label: "Height (inches)", group: "demographics" },
  { key: "weight_lbs", label: "Weight (lb)", group: "demographics" },
  { key: "employer_name", label: "Employer", group: "demographics" },
  { key: "occupation", label: "Occupation", group: "demographics" },
  {
    key: "individual_annual_income",
    label: "Annual income",
    group: "demographics",
  },
  {
    key: "referral_source_type",
    label: "Referral source type",
    group: "demographics",
  },
  {
    key: "referral_source_detail",
    label: "Referral source detail",
    group: "demographics",
  },
  {
    key: "real_estate_occupation",
    label: "Real estate occupation",
    group: "demographics",
  },
  {
    key: "drivers_license_state",
    label: "Driver's license state",
    group: "identity",
  },
  { key: "bank_account_type", label: "Bank account type", group: "comms" },
  // Driver discounts (migration 0226) — grouped with demographics since
  // they describe the person, not a communication preference.
  { key: "good_student", label: "Good Student discount", group: "demographics" },
  {
    key: "driver_training",
    label: "Driver Training discount",
    group: "demographics",
  },
  { key: "transactional_ok", label: "Transactional emails", group: "comms" },
  { key: "relational_ok", label: "Relational emails", group: "comms" },
  { key: "marketing_ok", label: "Marketing emails", group: "comms" },
  { key: "do_not_email", label: "Do not email", group: "comms" },
  { key: "sms_opt_in", label: "SMS opt-in", group: "comms" },
  { key: "tcpa_consent_at", label: "TCPA consent", group: "comms" },
  {
    key: "what_matters_most",
    label: "What matters most",
    group: "demographics",
  },
];

// ────────────────────────────────────────────────────────────────────
// Per-field choice + override composition
// ────────────────────────────────────────────────────────────────────

/**
 * What the user picked on a single row of the wizard.
 *
 *   * "primary" — keep survivor's value (the merge endpoint's default;
 *     no override sent).
 *   * "merged" — copy merged's value onto survivor.
 *   * "keep_both" — only valid for `email` and `phone_primary`. The
 *     wizard composes TWO overrides: keep survivor's primary, AND
 *     promote merged's primary into survivor's secondary slot.
 */
export type PerFieldChoice = "primary" | "merged" | "keep_both";

export type PerFieldChoiceMap = Partial<
  Record<MergeableContactField, PerFieldChoice>
>;

/** Fields that support the "Keep both" affordance. */
export const KEEP_BOTH_FIELDS = new Set<MergeableContactField>([
  "email",
  "phone_primary",
]);

/**
 * Treat null / undefined / empty-string as blank. Boolean false and
 * numeric zero are NOT blank (they're legitimate distinct values).
 * Mirrors the server-side guard in mergeContacts() so the wizard's
 * smart-default agrees with the backend's blank-protection rule.
 */
export function isBlank(value: unknown): boolean {
  return value === null || value === undefined || value === "";
}

/** True when the two values are NOT considered "the same" for merge purposes. */
export function valuesDiffer(a: unknown, b: unknown): boolean {
  // Treat blank-vs-blank as equal regardless of which flavor of blank
  // each side holds (null vs "" vs undefined). This matters because
  // the SafeContact projection normalizes most-but-not-every blank
  // to null; SQLite still allows "" through on legacy rows.
  if (isBlank(a) && isBlank(b)) return false;
  return a !== b;
}

/**
 * Given a survivor and a merged contact, return the subset of
 * mergeable fields where their values differ. Used to render Step 2's
 * "conflicts only" view; the "show all 25 fields" toggle bypasses
 * this and renders MERGEABLE_FIELDS_UI verbatim.
 */
export function detectConflicts(
  survivor: MergeContactShape,
  merged: MergeContactShape,
): MergeableContactField[] {
  const conflicts: MergeableContactField[] = [];
  for (const f of MERGEABLE_FIELDS_UI) {
    const s = (survivor as Record<string, unknown>)[f.key];
    const m = (merged as Record<string, unknown>)[f.key];
    if (valuesDiffer(s, m)) conflicts.push(f.key);
  }
  return conflicts;
}

/**
 * Smart default for a single field row. Per Grant 2026-05-05: if
 * survivor is blank but merged has a value, pre-select "merged" so
 * the wizard surfaces the populated value as the default winner.
 * Otherwise default "primary" (no override sent — survivor wins).
 *
 * Never defaults to "keep_both" — that's an explicit user choice on
 * email + phone conflicts.
 */
export function defaultPerFieldChoice(
  survivorValue: unknown,
  mergedValue: unknown,
): PerFieldChoice {
  if (isBlank(survivorValue) && !isBlank(mergedValue)) return "merged";
  return "primary";
}

/**
 * Build the full default per-field choice map for a survivor + merged
 * pair. Conflicts get the smart default; non-conflicts get "primary"
 * (effectively a no-op since the override map's missing keys also
 * default to "primary" on the server, but being explicit makes the
 * wizard's UI state easier to reason about).
 */
export function buildDefaultChoices(
  survivor: MergeContactShape,
  merged: MergeContactShape,
): PerFieldChoiceMap {
  const choices: PerFieldChoiceMap = {};
  for (const f of MERGEABLE_FIELDS_UI) {
    const s = (survivor as Record<string, unknown>)[f.key];
    const m = (merged as Record<string, unknown>)[f.key];
    choices[f.key] = defaultPerFieldChoice(s, m);
  }
  return choices;
}

/**
 * The full request body shape the merge-from endpoint expects.
 * `composeMergeRequest()` returns this rather than just the
 * field_overrides so the wizard can emit secondary_promotions
 * alongside in one structured payload.
 */
export interface ComposedMergeRequest {
  field_overrides: ContactFieldOverrides;
  secondary_promotions: { email?: boolean; phone?: boolean };
}

/**
 * Turn the wizard's PerFieldChoiceMap into the merge request shape.
 *
 * Special-case "keep_both" on email + phone_primary:
 *   * The primary slot stays at survivor's value (no override sent).
 *   * `secondary_promotions[email|phone]` is set so the backend
 *     promotes merged.email → primary.secondary_email AND/OR
 *     merged.phone_primary → primary.phone_secondary in the same
 *     transaction. (PR #303 added secondary_promotions; the value
 *     flows from merged's primary column into survivor's secondary
 *     column — a CROSS-column copy that field_overrides alone
 *     can't express.)
 *
 * Backend's blank-protection guard handles the edge case where the
 * merged side's source column is blank (silently no-ops).
 *
 * Caller is responsible for not setting "keep_both" on a field that
 * isn't in KEEP_BOTH_FIELDS — we silently degrade those to "primary"
 * to stay safe.
 */
export function composeMergeRequest(
  choices: PerFieldChoiceMap,
): ComposedMergeRequest {
  const overrides: ContactFieldOverrides = {};
  const secondary: { email?: boolean; phone?: boolean } = {};
  for (const [rawField, choice] of Object.entries(choices)) {
    const field = rawField as MergeableContactField;
    if (choice === "merged") {
      overrides[field] = "merged";
      continue;
    }
    if (choice === "keep_both") {
      if (!KEEP_BOTH_FIELDS.has(field)) {
        // Defensive: degrade to "primary" silently. The wizard's UI
        // shouldn't allow "keep_both" on non-eligible rows.
        continue;
      }
      // Survivor keeps the primary slot (backend default —
      // omitting from overrides is sufficient and keeps the audit
      // context free of no-op rows). Promote merged's same column
      // into the survivor's secondary slot via secondary_promotions.
      if (field === "email") secondary.email = true;
      if (field === "phone_primary") secondary.phone = true;
      continue;
    }
    // "primary" — leave the override unset. Backend default is
    // primary-wins on missing keys.
  }
  return { field_overrides: overrides, secondary_promotions: secondary };
}

// ────────────────────────────────────────────────────────────────────
// Cross-household + populated-count helpers
// ────────────────────────────────────────────────────────────────────

/**
 * Given a list of cluster members, return whether they all share one
 * household and the deduped list of distinct household_ids the
 * cluster touches. Drives the cross-household banner: when
 * `sameHousehold` is false, the wizard renders a navy banner
 * routing the admin to the household-merge picker instead.
 *
 * Contacts with `household_id === null` (orphan contacts not
 * belonging to any household) are counted as their own bucket via
 * the sentinel `__orphan` so the banner still fires when one cluster
 * member is in a household and another isn't.
 */
export function detectCrossHousehold(
  contacts: ReadonlyArray<{ household_id: string | null }>,
): { sameHousehold: boolean; householdIds: string[]; hasOrphan: boolean } {
  const ids = new Set<string>();
  let hasOrphan = false;
  for (const c of contacts) {
    if (c.household_id === null) {
      hasOrphan = true;
    } else {
      ids.add(c.household_id);
    }
  }
  const householdIds = Array.from(ids).sort();
  // "Same household" means everyone is in the same household and no
  // one is an orphan. A cluster of [orphan, household-A] is a
  // cross-household cluster from the wizard's perspective.
  const sameHousehold = !hasOrphan && householdIds.length === 1;
  return { sameHousehold, householdIds, hasOrphan };
}

/**
 * Count how many mergeable fields are populated (non-blank) on a
 * contact. Used by MergeReviewCard to surface "Robert Smith — 17/29
 * fields" so the user can pick the richest record as survivor.
 */
export function countPopulatedFields(contact: MergeContactShape): {
  populated: number;
  total: number;
} {
  let populated = 0;
  const total = MERGEABLE_FIELDS_UI.length;
  for (const f of MERGEABLE_FIELDS_UI) {
    const v = (contact as Record<string, unknown>)[f.key];
    if (!isBlank(v)) populated += 1;
  }
  return { populated, total };
}

// Phase B1 (May 2026 — data hygiene gap recovery): master-record
// auto-suggestion. Per the user's Perplexity research:
//
//   "Use clear merge rules to pick the surviving master, such as
//    most complete record, most recently updated record, record
//    with the most linked activity, or record with the highest
//    trust source. Systems that score candidate masters before
//    merge reduce inconsistent decisions and make the workflow
//    easier for staff to follow."
//
// Today's wizard required the user to manually pick the survivor
// for every merge. This helper produces a scored suggestion + a
// list of plain-English reasons, so the wizard can pre-pick the
// winner AND show the user WHY it picked that one.
//
// Scoring weights (intentionally simple — production data validates
// this, we'll re-tune in v1.1):
//   * +3 per populated field beyond the worst contact's count
//   * +2 if updated_at is more than 30 days newer than every other
//   * +1 if the contact has a non-blank email when the others don't
//   * +1 if the contact has a non-blank phone when the others don't
//
// Tiebreak: lowest contact id (stable across reloads).

export type SuggestableContactShape = MergeContactShape & {
  id: string;
  updated_at?: string | null;
  // Sensitive-data presence flags (from SafeContact). Encrypted
  // SSN / DL / bank values are encryption-scoped per-contact and are
  // PERMANENTLY LOST when their contact is the sidelined side of a
  // merge (Baker 2026-05-18). suggestSurvivor therefore refuses to
  // suggest a survivor that would drop a uniquely-held sensitive
  // record. Optional — when absent (legacy callers) the rule is a
  // no-op and scoring is unchanged.
  has_ssn_on_file?: boolean;
  has_spouse_ssn_on_file?: boolean;
  has_coborrower_ssn_on_file?: boolean;
  has_drivers_license_on_file?: boolean;
  has_bank_account_on_file?: boolean;
  has_bank_routing_on_file?: boolean;
};

function hasSensitiveOnFile(c: SuggestableContactShape): boolean {
  return (
    c.has_ssn_on_file === true ||
    c.has_spouse_ssn_on_file === true ||
    c.has_coborrower_ssn_on_file === true ||
    c.has_drivers_license_on_file === true ||
    c.has_bank_account_on_file === true ||
    c.has_bank_routing_on_file === true
  );
}

export interface SurvivorSuggestion {
  /** The id of the suggested survivor. */
  suggestedId: string;
  /** Human-readable reasons the wizard can render in the banner. */
  reasons: string[];
  /** All scored candidates so the UI can show "vs. other contact". */
  scores: Array<{
    id: string;
    populatedFields: number;
    score: number;
    updatedAt: string | null;
  }>;
}

/**
 * Score every contact in a cluster and pick the suggested survivor.
 * Returns a deterministic result — the same input always produces
 * the same output across reloads (tiebreak by lexically lowest id).
 *
 * Returns null only when the input array is empty (caller should
 * fall back to manual pick — there's nothing to merge anyway).
 */
export function suggestSurvivor(
  contacts: ReadonlyArray<SuggestableContactShape>,
): SurvivorSuggestion | null {
  if (contacts.length === 0) return null;
  if (contacts.length === 1) {
    const only = contacts[0]!;
    return {
      suggestedId: only.id,
      reasons: ["Only one contact — nothing to compare against."],
      scores: [
        {
          id: only.id,
          populatedFields: countPopulatedFields(only).populated,
          score: 0,
          updatedAt: only.updated_at ?? null,
        },
      ],
    };
  }

  // Per-contact base data
  const stats = contacts.map((c) => {
    const populated = countPopulatedFields(c).populated;
    const hasEmail = !isBlank(c.email);
    const hasPhone = !isBlank(c.phone_primary);
    const updatedAt = c.updated_at ?? null;
    return { id: c.id, populated, hasEmail, hasPhone, updatedAt };
  });

  // Find the highest populated count + most-recent updated_at across
  // the cluster so we can score relative-to-best.
  const minPopulated = Math.min(...stats.map((s) => s.populated));
  const newestUpdatedAt = stats
    .map((s) => s.updatedAt)
    .filter((t): t is string => t !== null && t !== "")
    .sort()
    .reverse()[0];

  // Score every contact.
  const scored = stats.map((s) => {
    let score = 0;
    score += (s.populated - minPopulated) * 3;
    if (
      s.updatedAt &&
      newestUpdatedAt &&
      s.updatedAt === newestUpdatedAt &&
      // Only score the recency bonus when the gap is >30 days, so
      // contacts updated within a month of each other don't get
      // arbitrarily ranked.
      stats.some(
        (other) =>
          other.id !== s.id &&
          other.updatedAt &&
          daysBetween(other.updatedAt, s.updatedAt!) > 30,
      )
    ) {
      score += 2;
    }
    if (
      s.hasEmail &&
      stats.some((other) => other.id !== s.id && !other.hasEmail)
    ) {
      score += 1;
    }
    if (
      s.hasPhone &&
      stats.some((other) => other.id !== s.id && !other.hasPhone)
    ) {
      score += 1;
    }
    return {
      id: s.id,
      populatedFields: s.populated,
      score,
      updatedAt: s.updatedAt,
    };
  });

  // Pick winner: highest score, ties broken by lexically lowest id.
  scored.sort((a, b) => {
    if (a.score !== b.score) return b.score - a.score;
    return a.id < b.id ? -1 : 1;
  });

  // PII safety override (Baker 2026-05-18): encrypted SSN / DL / bank
  // data is encryption-scoped per-contact and is PERMANENTLY LOST
  // when its contact is the sidelined side of a merge. If some
  // candidates carry sensitive data on file and others don't, the
  // survivor MUST be one that does — otherwise the merge silently
  // destroys unrecoverable PII. Restrict the winner pool accordingly
  // (the per-contact scores are still reported for the UI).
  const sensitiveById = new Map(
    contacts.map((c) => [c.id, hasSensitiveOnFile(c)]),
  );
  const someSensitive = scored.some((s) => sensitiveById.get(s.id));
  const someNonSensitive = scored.some((s) => !sensitiveById.get(s.id));
  const sensitiveOverride = someSensitive && someNonSensitive;
  const winner = sensitiveOverride
    ? (scored.find((s) => sensitiveById.get(s.id)) ?? scored[0]!)
    : scored[0]!;

  // Build human reasons. We compare the winner to the SECOND-place
  // contact (the runner-up the user would otherwise have picked).
  const runnerUp = scored.find((s) => s.id !== winner.id) ?? null;
  const reasons: string[] = [];
  if (sensitiveOverride) {
    reasons.push(
      "Keeps the record with SSN / bank data on file — sensitive fields are NOT transferable on merge (the other record would lose them).",
    );
  }
  if (runnerUp && winner.populatedFields > runnerUp.populatedFields) {
    reasons.push(
      `${winner.populatedFields} of ${MERGEABLE_FIELDS_UI.length} fields populated (vs ${runnerUp.populatedFields})`,
    );
  } else if (runnerUp && winner.populatedFields === runnerUp.populatedFields) {
    reasons.push(
      `Same field count (${winner.populatedFields}/${MERGEABLE_FIELDS_UI.length}) — tiebreak applied`,
    );
  }
  if (
    runnerUp &&
    winner.updatedAt &&
    runnerUp.updatedAt &&
    winner.updatedAt > runnerUp.updatedAt
  ) {
    const days = daysBetween(runnerUp.updatedAt, winner.updatedAt);
    if (days > 30) {
      reasons.push(
        `Updated ${formatDaysAgo(winner.updatedAt)} (${days} days newer than runner-up)`,
      );
    }
  }
  const winnerStat = stats.find((s) => s.id === winner.id)!;
  if (
    winnerStat.hasEmail &&
    stats.some((s) => s.id !== winner.id && !s.hasEmail)
  ) {
    reasons.push("Has email; runner-up does not");
  }
  if (
    winnerStat.hasPhone &&
    stats.some((s) => s.id !== winner.id && !s.hasPhone)
  ) {
    reasons.push("Has phone; runner-up does not");
  }
  if (reasons.length === 0) {
    reasons.push("Stable tiebreak by id (no clear winner on score)");
  }

  return {
    suggestedId: winner.id,
    reasons,
    scores: scored,
  };
}

// Selective merge (Baker 2026-06-26): given a cluster's member ids, the
// chosen survivor, and the set of members the steward marked "different
// person — keep separate", return the non-survivor ids that should
// actually be merged in (survivor-first order is irrelevant; the
// orchestrator iterates these). Pure so the include/exclude logic is
// unit-tested independent of React.
//
// Rules:
//   * the survivor is never a target (it's what others merge INTO);
//   * excluded members are kept fully separate (never targets);
//   * the survivor can't also be excluded — if it appears in the
//     excluded set (stale state), it's still treated as the survivor.
export function selectMergeTargets(
  memberIds: ReadonlyArray<string>,
  survivorId: string,
  excludedIds: ReadonlySet<string>,
): string[] {
  return memberIds.filter(
    (id) => id !== survivorId && !excludedIds.has(id),
  );
}

function daysBetween(a: string, b: string): number {
  const aT = Date.parse(a);
  const bT = Date.parse(b);
  if (Number.isNaN(aT) || Number.isNaN(bT)) return 0;
  return Math.abs(Math.round((bT - aT) / (1000 * 60 * 60 * 24)));
}

function formatDaysAgo(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (days < 1) return "today";
  if (days < 30) return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
}

// ────────────────────────────────────────────────────────────────────
// Compile-time guard: every UI key must be a server-side
// MergeableContactField. If the server's MERGEABLE_FIELDS shrinks
// without us removing a key here, this fails at typecheck.
// ────────────────────────────────────────────────────────────────────
const _UI_KEYS_ARE_SERVER_KEYS: ReadonlyArray<MergeableContactField> =
  MERGEABLE_FIELDS_UI.map((f) => f.key);
void _UI_KEYS_ARE_SERVER_KEYS;
